import type { Optional } from '@festina-life/core'

import type { useFilterCommands } from '../../hooks/useFilterCommands'
import { getLatestEvent } from '../../utility/eventUtils'
import type {
  EventSelection,
  EventSelectionWithApplyDate,
} from './commitmentEventSelectionContext'

type GetEventSelection = (
  commandAndEventData: ReturnType<typeof useFilterCommands>,
  eventSelectionId: string,
) => Optional<EventSelection>

const getEventSelectionFromEvents: GetEventSelection = (
  commandAndEventData,
  eventSelectionId,
) => {
  const previousEventId = commandAndEventData.events.find(
    (event) => event.eventId === eventSelectionId,
  )?.previousEventId

  return previousEventId ? { type: 'event', id: previousEventId } : undefined
}

const getEventSelectionFromPreviousUnappliedCommand: GetEventSelection = (
  commandAndEventData,
  eventSelectionId,
) => {
  const commandIndex = commandAndEventData.unappliedCommands.findIndex(
    (command) => command.commandId === eventSelectionId,
  )
  const previousUnappliedCommandId =
    commandAndEventData.unappliedCommands[commandIndex + 1]?.commandId

  return previousUnappliedCommandId
    ? {
        type: 'unappliedCommand',
        id: previousUnappliedCommandId,
      }
    : undefined
}

const getEventSelectionFromNewestEvent = (
  commandAndEventData: ReturnType<typeof useFilterCommands>,
): Optional<EventSelection> => {
  const newestEventId = commandAndEventData.events?.[0]?.eventId

  return newestEventId ? { type: 'event', id: newestEventId } : undefined
}

/**
 * Needed because when we show diffing we want the 'from' event to be part of the diff
 */
export const eventSelectionToPreviousEventSelection = (
  eventSelection: Optional<EventSelection>,
  commandAndEventData: ReturnType<typeof useFilterCommands>,
): Optional<EventSelection> =>
  eventSelection
    ? {
        event: getEventSelectionFromEvents(
          commandAndEventData,
          eventSelection.id,
        ),
        unappliedCommand:
          getEventSelectionFromPreviousUnappliedCommand(
            commandAndEventData,
            eventSelection.id,
          ) ?? // Fallback needed because when we show diffing we want the 'from' event to be part of the diff
          getEventSelectionFromNewestEvent(commandAndEventData),
      }[eventSelection.type]
    : null

/**
 * Intended for use in EventSelectionProvider components.
 *
 * Note that when used inside a `useEffect` to auto-move to latest,
 * the suspense boundary of children cannot escape above the effect,
 * or the component will remount and cause the effect to run as-if new.
 * */
export const getLatestEventSelection = (
  commandAndEventData: ReturnType<typeof useFilterCommands>,
): Optional<EventSelectionWithApplyDate> => {
  const latestEvent = getLatestEvent(commandAndEventData.events)

  return latestEvent?.eventId && latestEvent?.applyDate
    ? {
        id: latestEvent.eventId,
        type: 'event',
        applyDate: latestEvent.applyDate,
      }
    : undefined
}
